import { FC, useContext, useState } from 'react';
import { isRight } from 'fp-ts/lib/Either';
import BucketEntity from '../../../../domain/entities/BucketEntity';
import UserEntity from '../../../../domain/entities/UserEntity';
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent';
import ButtonComponent from '../../../components/button/ButtonComponent';
import { ButtonType } from '../../../components/button/ButtonComponentProps';
import Icons from '../../../assets/Icons';
import di from '../../../../di/DependencyInjection';
import MoveUserToBucketUseCase, { MoveUserToBucketUseCaseName } from '../../../../domain/use_cases/bucket/MoveUserToBucketUseCase';
import ModalsContext from '../../../../domain/providers/modal/ModalsContext';
import ModalsContextType from '../../../../domain/providers/modal/ModalsContextType';
import MoveUserModal from './MoveUserModal';

interface BucketUserPanelProps {
    selectedBucket: BucketEntity | null;
    users: UserEntity[];
    loading: boolean;
    allBuckets: BucketEntity[];
    onUserMoved: () => void;
    breadcrumb: string[];
    currentUserEmail: string;
}

const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const BucketUserPanel: FC<BucketUserPanelProps> = ({ selectedBucket, users, loading, allBuckets, onUserMoved, breadcrumb, currentUserEmail }) => {
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [userSearch, setUserSearch] = useState<string>('');
    const { openModalCustom, closeModalCustom, addToast } = useContext(ModalsContext) as ModalsContextType;

    const _handleToggleUser = (userId: string) => {
        const next = new Set(selectedUsers);
        if (next.has(userId)) {
            next.delete(userId);
        } else {
            next.add(userId);
        }
        setSelectedUsers(next);
    };

    const _handleSelectAll = () => {
        if (selectedUsers.size === users.length) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(users.map(u => u.email ?? u.id)));
        }
    };

    const _handleMoveConfirm = async (targetBucket: BucketEntity) => {
        let successCount = 0;
        const userIds = Array.from(selectedUsers);
        for (let i = 0; i < userIds.length; i++) {
            const userId = userIds[i];
            // Find the user's current data to preserve required fields
            const user = users.find(u => (u.email ?? u.id) === userId);
            if (!user) continue;
            const result = await di.get<MoveUserToBucketUseCase>(MoveUserToBucketUseCaseName).call(
                user.email ?? userId,
                targetBucket.name,
                targetBucket.name,
                ''
            );
            if (isRight(result)) successCount++;
        }
        closeModalCustom();
        setSelectedUsers(new Set());
        addToast(`Moved ${successCount} user(s) successfully`, 'success', 5000);
        onUserMoved();
    };

    const _handleOpenMoveModal = () => {
        if (selectedUsers.size === 0) {
            addToast('Select at least one user to move', 'error', 3000);
            return;
        }
        // Prevent moving yourself
        if (selectedUsers.has(currentUserEmail.toLowerCase()) || selectedUsers.has(currentUserEmail)) {
            addToast('You cannot move your own account. This would change your bucket visibility.', 'error', 5000);
            return;
        }
        openModalCustom('md', 'Move Users', (
            <MoveUserModal
                allBuckets={allBuckets}
                selectedCount={selectedUsers.size}
                onConfirm={_handleMoveConfirm}
                onCancel={closeModalCustom}
            />
        ));
    };

    const filteredUsers = userSearch
        ? users.filter(u => u.name.toLowerCase().includes(userSearch.toLowerCase()) || (u.email ?? '').toLowerCase().includes(userSearch.toLowerCase()))
        : users;

    if (!selectedBucket) {
        return (
            <div className="bucket_user_panel_empty">
                <div className="text-muted text-center p-4">
                    <Icons.People width={48} height={48} />
                    <p className="mt-2">Select a bucket to view users</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bucket_user_panel">
            {breadcrumb.length > 0 && (
                <div className="bucket_breadcrumb">
                    {breadcrumb.map((segment, i) => (
                        <span key={i}>
                            <span className={i === breadcrumb.length - 1 ? 'breadcrumb_active' : 'breadcrumb_item'}>{segment}</span>
                            {i < breadcrumb.length - 1 && <span className="breadcrumb_sep">›</span>}
                        </span>
                    ))}
                </div>
            )}

            <div className="bucket_user_panel_header">
                <div className="panel_header_left">
                    <strong>Users in: {selectedBucket.name}</strong>
                    <span className="badge bg-primary ms-2">{users.length}</span>
                </div>
            </div>

            {loading ? <LoadingComponent /> : (
                <>
                    <div className="bucket_user_panel_toolbar">
                        <input
                            type="text"
                            className="form-control user_search_input"
                            placeholder="Filter users..."
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                        />
                        <ButtonComponent text={selectedUsers.size === users.length ? "Deselect" : "Select All"} type={ButtonType.MAIN} onClick={_handleSelectAll} icon={<Icons.People />} />
                        <ButtonComponent text={`Move (${selectedUsers.size})`} type={ButtonType.MAIN} onClick={_handleOpenMoveModal} icon={<Icons.EditPencil />} />
                    </div>

                    <div className="bucket_user_list">
                        {filteredUsers.length === 0 && <div className="text-muted p-3">No users found</div>}
                        {filteredUsers.map((user, index) => (
                            <div
                                key={user.id ?? index}
                                className={`bucket_user_item ${selectedUsers.has(user.email ?? user.id) ? 'selected' : ''} ${(user.email ?? '').toLowerCase() === currentUserEmail.toLowerCase() ? 'is_current_user' : ''}`}
                                onClick={() => _handleToggleUser(user.email ?? user.id)}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedUsers.has(user.email ?? user.id)}
                                    readOnly
                                    className="user_checkbox"
                                />

                                <div className="user_avatar">
                                    {user.image || user.profile_picture ? (
                                        <img src={user.image || user.profile_picture} alt="" className="user_avatar_img" />
                                    ) : (
                                        <span className="user_avatar_initials">{getInitials(user.name)}</span>
                                    )}
                                </div>

                                <div className="bucket_user_info">
                                    <span className="bucket_user_name">{user.name}</span>
                                    <span className="bucket_user_email">{user.email}</span>
                                </div>

                                <div className="bucket_user_meta">
                                    {user.role && (
                                        <span className="user_role_tag">{user.role.name}</span>
                                    )}
                                    {!user.role_id && (
                                        <span className="badge bg-warning text-dark">No Role</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default BucketUserPanel;
